import React, { useEffect, useState, useRef } from 'react';
import ChatContent from './ChatContent';
import ToastNotification from './helpers/ToastNotification';
import { ChatIcon, CloseIcon } from './icons/ChatIcons';
import { 
  getChatContainerStyle, 
  getChatButtonStyle, 
  getIconAnimationStyle,
  getDefaultStyles
} from './styles/ChatStyles';
import { ChatProps, ChatMessage, ChatGroup } from './types/ChatTypes';
import { createTranslator, Language } from '../lib/i18n';
import { saveLanguageToCookie } from '../lib/i18n/languageDetection';

const Chat = ({ config }: ChatProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isIconAnimating, setIsIconAnimating] = useState(false);
  const [shouldShowCloseIcon, setShouldShowCloseIcon] = useState(false);
  const [chatGroups, setChatGroups] = useState<ChatGroup[]>([]);
  const [activeChatGroupId, setActiveChatGroupId] = useState<number | null>(null);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [chatGroupsPage, setChatGroupsPage] = useState(1);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<Language>((config.langCode || 'ro') as Language);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [currentEncouragementMessage, setCurrentEncouragementMessage] = useState(0);
  const [prevEncouragementMessage, setPrevEncouragementMessage] = useState(0);
  const [messageAnimation, setMessageAnimation] = useState('slide-up');
  const [animationKey, setAnimationKey] = useState(0);

  const chatRef = useRef<HTMLDivElement>(null);

  // Get configuration values
  const userId = config.userId || '';
  const websiteName = config.websiteName || '';
  const langCode = currentLanguage;
  const styleConfig = config.style || getDefaultStyles();
  const supportedLanguages = config.supportedLanguages;

  // Create translation function for this component
  const t = createTranslator(langCode);

  // Extract style values with defaults
  const primaryColor = styleConfig.primaryColor || '#000000';
  const secondaryColor = styleConfig.secondaryColor || '#000000';
  const backgroundColor = styleConfig.backgroundColor || '#ffffff';
  const userMessageColor = styleConfig.userMessageColor || '#e3effd';
  const userMessageTextColor = styleConfig.userMessageTextColor || '#000000';
  const botMessageColor = styleConfig.botMessageColor || '#f5f5f5';
  const botMessageTextColor = styleConfig.botMessageTextColor || '#333333';
  const fontFamily = styleConfig.fontFamily || 'Arial, sans-serif';
  const fontSize = styleConfig.fontSize || '16px';
  const borderRadius = styleConfig.borderRadius || '16px';
  const headerText = styleConfig.headerText || t('conversation');
  const roundedChatButton = styleConfig.roundedChatButton !== undefined ? styleConfig.roundedChatButton : true;

  // Create style object with all values
  const style = {
    primaryColor,
    secondaryColor,
    backgroundColor,
    userMessageColor,
    userMessageTextColor,
    botMessageColor,
    botMessageTextColor,
    fontFamily,
    fontSize,
    borderRadius,
    headerText,
    roundedChatButton
  };

  async function getChatGroups() {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/get-user-chat-groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': process.env.REACT_APP_API_KEY || ''
        },
        body: JSON.stringify({ 
          user_id: userId,
          website_name: websiteName,
          page: 1,
          lang_code: langCode
        })
      });

      const res = await response.json();
      const data = res?.data;
      
      setChatGroups(data);
      setChatGroupsPage(2);
      // Set the active chat group to the first one if available
      if (data.length > 0 && !activeChatGroupId) {
        setActiveChatGroupId(data[0].id);
        setConversationId(data[0].conversation_id);
        await getChatMessages(data[0].id);
      } else if (data.length === 0) {
        setMessages([
          { content: t('welcomeMessage'), sender: 'AI', type: 'String' },
        ]);
      }

    } catch (error) {
      console.error('Error fetching chat groups:', error);
    }
  }

  async function getChatMessages(chatGroupId: number) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/get-chat-group-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': process.env.REACT_APP_API_KEY || ''
        },
        body: JSON.stringify({ 
          chat_group_id: chatGroupId,
          website_name: websiteName,
          lang_code: langCode
        })
      });

      const data = await response.json();
      const currentChatGroupMessages = JSON.parse(data[0].messages);

      const messages = currentChatGroupMessages.map((message: any) => ({
        content: message.message,
        sender: message.role,
        type: message.type || 'String'
      }));

      setMessages(messages);
    } catch (error) {
      console.error('Error fetching chat messages:', error);
    }
  }

  async function createNewChatGroup() {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/create-chat-group`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': process.env.REACT_APP_API_KEY || ''
        },
        body: JSON.stringify({ 
          user_id: userId,
          website_name: websiteName,
          title: t('defaultChatTitle', { timestamp: new Date().toLocaleString() }), // Default title with timestamp
          lang_code: langCode
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create chat group: ${response.status}`);
      }

      const res = await response.json();
      const newChatGroup = res?.chat_group;
      
      // Add the new chat group to the list and set it as active
      setChatGroups(prevGroups => [newChatGroup, ...prevGroups]);
      setActiveChatGroupId(newChatGroup.id);
      setConversationId(newChatGroup?.conversation_id);
      // Set welcome message without timestamp
      setMessages([
        { 
          content: t('welcomeMessage'), 
          sender: 'bot',
          type: 'String'
        },
      ]);

      return newChatGroup.id;
    } catch (error) {
      console.error('Error creating new chat group:', error);
      // Show a simple error message to the user
      alert(t('failedCreateChat'));
      return null;
    }
  }

  async function loadMoreChats() {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/get-user-chat-groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': process.env.REACT_APP_API_KEY || ''
        },
        body: JSON.stringify({
          user_id: userId,
          website_name: websiteName,
          page: chatGroupsPage,
          lang_code: langCode
        })
      });

      const res = await response.json();
      const data = res?.data;

      setChatGroupsPage(chatGroupsPage + 1);
      setChatGroups((prevGroups) => [...prevGroups, ...data]);
      
      if (data.length > 0) {
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error loading more chats:', error);
      return false;
    }
  }

  const handleSendMessage = async (message: string) => {
    // If there's no active chat group, create one first
    let currentChatGroupId = activeChatGroupId;
    if (!currentChatGroupId) {
      currentChatGroupId = await createNewChatGroup();
      if (!currentChatGroupId) return; // Failed to create a new chat group
    }

    // Create a new message object
    const newMessage: ChatMessage = {
      content: message,
      sender: 'user',
      type: 'String'
    };

    // Add to local messages immediately for UI responsiveness
    setMessages(prevMessages => [...prevMessages, newMessage]);

    try {
      // Send to server
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': process.env.REACT_APP_API_KEY || ''
        },
        body: JSON.stringify({
          user_id: userId,
          website_name: websiteName,
          chat_group_id: currentChatGroupId,
          message: message,
          type: 'String',
          lang_code: langCode
        })
      });

      if (!response.ok) {
        console.error('Failed to send message: Server returned', response.status);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleChatGroupChange = async (chatGroupId: number) => {
    // Special case: 0 means create a new chat group
    if (chatGroupId === 0) {
      const newChatGroupId = await createNewChatGroup();
      setActiveChatGroupId(newChatGroupId);
      return;
    }
    
    setActiveChatGroupId(chatGroupId);
    await getChatMessages(chatGroupId);
  };

  useEffect(() => {
    // Add hover effect with the secondary color
    const chatWrapper = chatRef.current?.querySelector('.chat-wrapper') as HTMLElement;
    
    if (chatWrapper) {
      // Store original background color
      const originalBackground = primaryColor;
      
      chatWrapper.addEventListener('mouseenter', () => {
        chatWrapper.style.background = secondaryColor;
        chatWrapper.style.transform = 'scale(1.05)';
      });
      
      chatWrapper.addEventListener('mouseleave', () => {
        chatWrapper.style.background = originalBackground;
        chatWrapper.style.transform = 'scale(1)';
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, config.userId, config.websiteName, primaryColor, secondaryColor, activeChatGroupId]);

  useEffect(() => {
    getChatGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timer for chat encouragement messages (cycling every 5 seconds)
  useEffect(() => {
    // Only show encouragement if chat has never been opened and is currently closed
    if (!hasBeenOpened && !isOpen) {
      // Start showing first message after 5 seconds
      const initialTimer = setTimeout(() => {
        setCurrentEncouragementMessage(1);
      }, 2000); // 3 seconds

      // Then cycle through messages every 5 seconds
      const cycleTimer = setInterval(() => {
        setPrevEncouragementMessage(currentEncouragementMessage);
        setCurrentEncouragementMessage(prev => (prev % 3) + 1);
      }, 4000); // Every 3 seconds

      return () => {
        clearTimeout(initialTimer);
        clearInterval(cycleTimer);
      };
    }
    // eslint-disable-next-line
  }, [hasBeenOpened, isOpen]);

  // Hide encouragement when chat is opened
  useEffect(() => {
    if (isOpen && currentEncouragementMessage > 0) {
      setCurrentEncouragementMessage(0);
      setPrevEncouragementMessage(0);
    }
  }, [isOpen, currentEncouragementMessage]);

  // Determine which animation to use for message changes
  useEffect(() => {
    if (currentEncouragementMessage > 0) {
      if (prevEncouragementMessage === 0) {
        // First appearance - use slide-up animation
        setMessageAnimation('slide-up');
      } else {
        // Message change - use message-change animation
        setMessageAnimation('message-change');
      }
      // Force re-render to replay animation
      setAnimationKey(prev => prev + 1);
    }
  }, [currentEncouragementMessage, prevEncouragementMessage]);

  // Handle clicks outside the chat component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && chatRef.current && !chatRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        handleIconTransition(false);
      }
    };

    // Handle escape key press
    const handleEscKey = (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Escape') {
        setIsOpen(false);
        handleIconTransition(false);
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    // Clean up event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  const handleIconTransition = (opening: boolean) => {
    setIsIconAnimating(true);

    // First, animate the current icon out
    setTimeout(() => {
      // Update which icon should be shown
      setShouldShowCloseIcon(opening);

      // After a short delay, start animating the new icon in
      setTimeout(() => {
        setIsIconAnimating(false);
      }, 50);
    }, 150);
  };

  const toggleChat = () => {
    const willBeOpen = !isOpen;

    // Mark as opened when first opened
    if (willBeOpen && !hasBeenOpened) {
      setHasBeenOpened(true);
    }

    handleIconTransition(willBeOpen);
    setIsOpen(willBeOpen);
  };

  const closeChat = () => {
    setIsOpen(false);
    handleIconTransition(false);
  };

  const handleLanguageChange = (newLanguage: Language) => {
    setCurrentLanguage(newLanguage);
    saveLanguageToCookie(newLanguage);
    
    // Optionally reload chat groups and messages with new language
    getChatGroups();
  };

  return (
    <div ref={chatRef} style={getChatContainerStyle(style)}>
      {isOpen && (
        <ChatContent
          messages={messages}
          setMessages={setMessages}
          onClose={closeChat}
          onSendMessage={handleSendMessage}
          style={style}
          chatGroups={chatGroups}
          activeChatGroupId={activeChatGroupId}
          onChatGroupChange={handleChatGroupChange}
          //@ts-ignore
          conversationId={conversationId}
          setConversationId={setConversationId}
          onLoadMoreChats={loadMoreChats}
          userId={userId}
          websiteName={websiteName}
          toastMessage={toastMessage}
          setToastMessage={setToastMessage}
          showToast={showToast}
          setShowToast={setShowToast}
          langCode={langCode}
          supportedLanguages={supportedLanguages}
          onLanguageChange={handleLanguageChange}
        />
      )}
      
      {/* Toast notification positioned to the left of chat button */}
      <ToastNotification
        toastMessage={toastMessage}
        showToast={showToast}
      />

      {/* Chat encouragement message */}
      {currentEncouragementMessage > 0 && !isOpen && (
        <div
          key={animationKey}
          style={{
            position: 'absolute',
            bottom: '70px',
            right: '0',
            background: style.primaryColor,
            color: 'white',
            padding: '8px 12px',
            borderRadius: '16px',
            fontSize: '14px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            zIndex: 9001,
            animation: `${messageAnimation} 0.4s ease-out`,
            pointerEvents: 'none',
          }}
        >
          {t(`chatEncouragement${currentEncouragementMessage}` as any)}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: '12px',
              width: '0',
              height: '0',
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid ${style.primaryColor}`,
            }}
          />
        </div>
      )}

      <div
        className="chat-wrapper"
        onClick={toggleChat}
        style={getChatButtonStyle(style, isOpen)}
      >
        <div style={getIconAnimationStyle(isIconAnimating)}>
          {!shouldShowCloseIcon ? (
            config.chatIconSvg ? (
              <div dangerouslySetInnerHTML={{ __html: config.chatIconSvg }} />
            ) : (
              <ChatIcon color="white" />
            )
          ) : (
            config.closeIconSvg ? (
              <div dangerouslySetInnerHTML={{ __html: config.closeIconSvg }} />
            ) : (
              <CloseIcon color="white" />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;
